import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from '../../utils/i18n-utils';
import { 
  getExpenses, 
  getExpenseCategories, 
  createExpense, 
  updateExpense, 
  deleteExpense,
  Expense,
  ExpenseCategory
} from '../../api/accounting';
import { format } from 'date-fns';

const Expenses: React.FC = () => {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [categoryLoading, setCategoryLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentExpense, setCurrentExpense] = useState<Partial<Expense>>({});
  const [expenseDate, setExpenseDate] = useState<Date | null>(null);
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);
  const [filterCategory, setFilterCategory] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
  }, []);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const startDate = filterStartDate ? format(filterStartDate, 'yyyy-MM-dd') : undefined;
      const endDate = filterEndDate ? format(filterEndDate, 'yyyy-MM-dd') : undefined;
      const data = await getExpenses(startDate, endDate, filterCategory || undefined);
      setExpenses(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch expenses:', err);
      setError('Failed to load expenses. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setCategoryLoading(true);
      const data = await getExpenseCategories();
      setCategories(data);
    } catch (err: any) {
      console.error('Failed to fetch expense categories:', err);
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchExpenses();
  };

  const handleClearFilters = () => {
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterCategory(null);
    // Fetch all expenses
    fetchExpenses();
  };

  const handleOpenDialog = (mode: 'add' | 'edit', expense?: Expense) => {
    setDialogMode(mode);
    
    if (mode === 'edit' && expense) {
      setCurrentExpense(expense);
      setExpenseDate(expense.date ? new Date(expense.date) : null);
    } else {
      setCurrentExpense({
        amount: 0,
        paymentMethod: 'cash',
        isRecurring: false,
        expenseCategoryId: categories.length > 0 ? categories[0].id : undefined
      });
      setExpenseDate(new Date());
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentExpense({
      ...currentExpense,
      [name]: value
    });
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Convert string booleans to actual booleans
    if (value === "true" || value === "false") {
      processedValue = value === "true";
    }
    
    setCurrentExpense({
      ...currentExpense,
      [name]: processedValue
    });
  };

  const handleSubmit = async () => {
    if (!currentExpense.amount || !expenseDate || !(currentExpense.expenseCategoryId || currentExpense.ExpenseCategoryId)) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields.',
        severity: 'error'
      });
      return;
    }
    
    try {
      const expenseData = {
        ...currentExpense,
        date: format(expenseDate, 'yyyy-MM-dd'),
        amount: Number(currentExpense.amount),
        ExpenseCategoryId: currentExpense.expenseCategoryId || currentExpense.ExpenseCategoryId
      };
      
      if (dialogMode === 'add') {
        await createExpense(expenseData);
        setSnackbar({
          open: true,
          message: 'Expense added successfully!',
          severity: 'success'
        });
      } else {
        if (currentExpense.id) {
          await updateExpense(currentExpense.id, expenseData);
          setSnackbar({
            open: true,
            message: 'Expense updated successfully!',
            severity: 'success'
          });
        }
      }
      
      handleCloseDialog();
      fetchExpenses();
    } catch (err: any) {
      console.error('Error saving expense:', err);
      setSnackbar({
        open: true,
        message: 'Failed to save expense. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleDeleteExpense = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await deleteExpense(id);
        setSnackbar({
          open: true,
          message: 'Expense deleted successfully!',
          severity: 'success'
        });
        fetchExpenses();
      } catch (err: any) {
        console.error('Error deleting expense:', err);
        setSnackbar({
          open: true,
          message: 'Failed to delete expense. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const getCategoryNameById = (id: number | undefined): string => {
    if (!id) return 'Unknown';
    const category = categories.find(cat => cat.id === id);
    return category ? category.name : 'Unknown';
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                mb: 0.5,
                color: '#101D68',
              }}
            >
              {t('navigation.accounting.expenses')}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#6B7280',
              }}
            >
              Track and manage business expenses
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
            sx={{
              background: 'linear-gradient(135deg, #101D68 0%, #2A3A8A 100%)',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0px 4px 12px rgba(16, 29, 104, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0A1445 0%, #101D68 100%)',
                boxShadow: '0px 6px 16px rgba(16, 29, 104, 0.4)',
              },
            }}
          >
            {t('expenses.addExpense')}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('common.filters')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 6 }}>
        <DatePicker
              label={t('common.startDate')}
              value={filterStartDate}
              onChange={(date) => setFilterStartDate(date)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
          <DatePicker
              label={t('common.endDate')}
              value={filterEndDate}
              onChange={(date) => setFilterEndDate(date)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
              <InputLabel>{t('expenses.category')}</InputLabel>
              <Select
                value={filterCategory || ''}
                onChange={(e) => setFilterCategory(e.target.value ? Number(e.target.value) : null)}
                label={t('expenses.category')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyFilters}
              >
                {t('common.apply')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
              >
                {t('common.clear')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('expenses.date')}</TableCell>
                <TableCell>{t('expenses.category')}</TableCell>
                <TableCell>{t('expenses.description')}</TableCell>
                <TableCell>{t('expenses.paymentMethod')}</TableCell>
                <TableCell align="right">{t('expenses.amount')}</TableCell>
                <TableCell>{t('expenses.recurring')}</TableCell>
                <TableCell align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell>{format(new Date(expense.date), 'dd/MM/yyyy')}</TableCell>
                  <TableCell>
                    {expense.ExpenseCategory ? 
                      expense.ExpenseCategory.name : 
                      (expense.expenseCategory ? 
                        expense.expenseCategory.name : 
                        getCategoryNameById(expense.ExpenseCategoryId || expense.expenseCategoryId))}
                  </TableCell>
                  <TableCell>{expense.description || '-'}</TableCell>
                  <TableCell>{t(`expenses.paymentMethods.${expense.paymentMethod}`)}</TableCell>
                  <TableCell align="right">د.ل{expense.amount.toFixed(2)}</TableCell>
                  <TableCell>
                    {expense.isRecurring && (
                      <Chip
                        size="small"
                        icon={<RepeatIcon />}
                        label={expense.recurringFrequency ? t(`expenses.frequency.${expense.recurringFrequency}`) : t('expenses.recurring')}
                        color="primary"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('common.edit')}>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleOpenDialog('edit', expense)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteExpense(expense.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {expenses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {t('expenses.noExpenses')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Expense Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? t('expenses.addExpense') : t('expenses.editExpense')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Box sx={{ mb: 2 }}>
              <DatePicker 
                label={t('expenses.date')}
                value={expenseDate}
                onChange={(date) => setExpenseDate(date)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
            <FormControl fullWidth margin="dense">
              <InputLabel>{t('expenses.category')}</InputLabel>
              <Select
                name="expenseCategoryId"
                value={currentExpense.expenseCategoryId || ''}
                onChange={handleSelectChange}
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              name="amount"
              label={t('expenses.amount')}
              fullWidth
              required
              type="number"
              inputProps={{ step: "0.01" }}
              value={currentExpense.amount || ''}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: 'د.ل',
              }}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>{t('expenses.paymentMethod')}</InputLabel>
              <Select
                name="paymentMethod"
                value={currentExpense.paymentMethod || 'cash'}
                onChange={handleSelectChange}
              >
                <MenuItem value="cash">{t('expenses.paymentMethods.cash')}</MenuItem>
                <MenuItem value="bank">{t('expenses.paymentMethods.bank')}</MenuItem>
                <MenuItem value="credit card">{t('expenses.paymentMethods.credit card')}</MenuItem>
                <MenuItem value="other">{t('expenses.paymentMethods.other')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              name="description"
              label={t('expenses.description')}
              fullWidth
              multiline
              rows={2}
              value={currentExpense.description || ''}
              onChange={handleInputChange}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>{t('expenses.recurring')}</InputLabel>
              <Select
                name="isRecurring"
                value={currentExpense.isRecurring === undefined ? "false" : currentExpense.isRecurring ? "true" : "false"}
                onChange={handleSelectChange}
              >
                <MenuItem value="false">{t('common.no')}</MenuItem>
                <MenuItem value="true">{t('common.yes')}</MenuItem>
              </Select>
            </FormControl>
            {currentExpense.isRecurring && (
              <FormControl fullWidth margin="dense">
                <InputLabel>{t('expenses.frequency')}</InputLabel>
                <Select
                  name="recurringFrequency"
                  value={currentExpense.recurringFrequency || 'monthly'}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="daily">{t('expenses.frequency.daily')}</MenuItem>
                  <MenuItem value="weekly">{t('expenses.frequency.weekly')}</MenuItem>
                  <MenuItem value="monthly">{t('expenses.frequency.monthly')}</MenuItem>
                  <MenuItem value="quarterly">{t('expenses.frequency.quarterly')}</MenuItem>
                  <MenuItem value="annually">{t('expenses.frequency.annually')}</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Expenses; 